import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  BugReport as BugReportIcon,
  Email as EmailIcon,
  ErrorOutline as ErrorOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';

type LogEntry = NonNullable<Awaited<ReturnType<typeof apiService.getSymfonyLogs>>['entries']>[number];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} o`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} Ko`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatModifiedAt(value: string): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('fr-FR');
}

function extractDateFromProdLog(fileName: string): string | null {
  const match = fileName.match(/^prod-(\d{4}-\d{2}-\d{2})\.log$/);
  return match ? match[1] : null;
}

function formatHourLabel(hour: number): string {
  return `${hour.toString().padStart(2, '0')}h00 – ${hour.toString().padStart(2, '0')}h59`;
}

const LINE_OPTIONS = [100, 200, 300, 500, 1000];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => hour);

type LogPreset = {
  id: string;
  label: string;
  search: string;
  file?: string;
};

const LOG_PRESETS: LogPreset[] = [
  { id: 'all', label: 'Tout', search: '' },
  { id: 'critical', label: 'Critiques', search: '.CRITICAL:' },
  { id: 'errors', label: 'Erreurs', search: '.ERROR:' },
  { id: 'warnings', label: 'Avertissements', search: '.WARNING:' },
  { id: 'security', label: 'Sécurité', search: 'AccessDenied' },
  { id: 'mail', label: 'Mails', search: 'email' },
  { id: 'db', label: 'Base', search: 'Doctrine' },
  { id: 'indexnow', label: 'IndexNow', search: 'IndexNow' },
  { id: 'providers', label: 'Partenaires', search: 'Prestataire' },
  { id: 'colissimo', label: 'Colissimo', search: 'Colissimo' },
  { id: 'stripe', label: 'Stripe', search: 'Stripe' },
  { id: 'deprecations', label: 'Dépréciations', search: '', file: 'deprecation.log' },
];

const RUNTIME_LOG_PRESETS: LogPreset[] = [
  { id: 'all', label: 'Tout', search: '' },
  { id: 'errors', label: 'HTTP 5xx', search: '"status":5' },
  { id: 'client_errors', label: 'HTTP 4xx', search: '"status":4' },
  { id: 'rotation', label: 'Rotation FPM', search: '"rotation_alert":true' },
];

function isRuntimeLogFile(fileName: string): boolean {
  return fileName === 'runtime.log';
}

function getPresetsForFile(fileName: string): LogPreset[] {
  return isRuntimeLogFile(fileName) ? RUNTIME_LOG_PRESETS : LOG_PRESETS;
}

function levelColors(level: string): { bg: string; fg: string; soft: string } {
  const upper = level.toUpperCase();
  if (upper === 'CRITICAL' || upper === 'EMERGENCY' || upper === 'ALERT') {
    return { bg: '#7f1d1d', fg: '#fff', soft: '#fecaca' };
  }
  if (upper === 'ERROR') {
    return { bg: '#dc2626', fg: '#fff', soft: '#fee2e2' };
  }
  if (upper === 'WARNING' || upper === 'WARN') {
    return { bg: '#d97706', fg: '#fff', soft: '#fef3c7' };
  }
  if (upper === 'INFO' || upper === 'NOTICE') {
    return { bg: '#2563eb', fg: '#fff', soft: '#dbeafe' };
  }
  return { bg: '#64748b', fg: '#fff', soft: '#e2e8f0' };
}

function categoryMeta(category: string): { color: string; icon: React.ReactNode } {
  switch (category) {
    case 'Security':
      return { color: '#7c3aed', icon: <LockIcon sx={{ fontSize: 18 }} /> };
    case 'Mail':
      return { color: '#0d9488', icon: <EmailIcon sx={{ fontSize: 18 }} /> };
    case 'Database':
      return { color: '#0369a1', icon: <StorageIcon sx={{ fontSize: 18 }} /> };
    case 'Deploy':
    case 'Bug':
      return { color: '#b91c1c', icon: <BugReportIcon sx={{ fontSize: 18 }} /> };
    case 'Erreur':
      return { color: '#dc2626', icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} /> };
    default:
      return { color: '#64748b', icon: <WarningAmberIcon sx={{ fontSize: 18 }} /> };
  }
}

function rowBackground(entry: LogEntry): string {
  if (entry.is_critical_alert || entry.severity === 'critical') {
    return 'rgba(254, 202, 202, 0.35)';
  }
  if (entry.severity === 'error') {
    return 'rgba(254, 226, 226, 0.25)';
  }
  if (entry.severity === 'warning') {
    return 'rgba(254, 243, 199, 0.35)';
  }
  return '#fff';
}

const SymfonyLogsPage: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState('');
  const [lines, setLines] = useState(500);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dateInput, setDateInput] = useState('');
  const [appliedDate, setAppliedDate] = useState('');
  const [hourInput, setHourInput] = useState<number | ''>('');
  const [appliedHour, setAppliedHour] = useState<number | ''>('');
  const [expandedRaw, setExpandedRaw] = useState<Record<number, boolean>>({});
  const [showRawFallback, setShowRawFallback] = useState(false);

  const { data: filesData } = useQuery({
    queryKey: ['symfony-logs-files'],
    queryFn: () => apiService.getSymfonyLogs({}),
  });

  const files = useMemo(() => filesData?.files ?? [], [filesData?.files]);

  useEffect(() => {
    if (!selectedFile && files.length > 0) {
      setSelectedFile(files[0].name);
    }
  }, [files, selectedFile]);

  useEffect(() => {
    const fileDate = extractDateFromProdLog(selectedFile);
    if (fileDate) {
      setDateInput(fileDate);
      setAppliedDate(fileDate);
    }
  }, [selectedFile]);

  useEffect(() => {
    if (!isRuntimeLogFile(selectedFile)) {
      return;
    }
    if (appliedSearch === '.ERROR:' || appliedSearch === '.WARNING:' || appliedSearch === '.CRITICAL:') {
      setSearchInput('');
      setAppliedSearch('');
    }
  }, [selectedFile, appliedSearch]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['symfony-logs', selectedFile, lines, appliedSearch, appliedDate, appliedHour],
    queryFn: () =>
      apiService.getSymfonyLogs({
        file: selectedFile,
        lines,
        search: appliedSearch || undefined,
        date: appliedDate || undefined,
        hour: appliedHour === '' ? undefined : appliedHour,
      }),
    enabled: Boolean(selectedFile),
  });

  const selectedFileMeta = useMemo(
    () => files.find((file) => file.name === selectedFile),
    [files, selectedFile],
  );

  const entries = useMemo(() => {
    if (data?.entries && data.entries.length > 0) {
      return [...data.entries].reverse();
    }
    return (data?.lines ?? []).map((raw) => ({
      datetime: null,
      datetime_label: null,
      timezone: null,
      level: 'INFO',
      channel: 'app',
      category: 'App',
      severity: 'info',
      message: raw,
      message_secondary: null,
      exception: null,
      exception_chain: null,
      http_code: null,
      http_label: null,
      file: null,
      file_path: null,
      line: null,
      is_critical_alert: false,
      raw,
    })) as LogEntry[];
  }, [data]);

  const handleApplyFilters = () => {
    const nextDate = dateInput.trim();
    const nextSearch = searchInput.trim();

    if (nextDate) {
      const matchingFile = files.find((file) => file.name === `prod-${nextDate}.log`);
      if (matchingFile) {
        setSelectedFile(matchingFile.name);
      }
    }

    setAppliedSearch(nextSearch);
    setAppliedDate(nextDate);
    setAppliedHour(hourInput);
  };

  const preferredLogFile = () =>
    files.find((file) => file.name.startsWith('prod-'))
    ?? files.find((file) => file.name === 'dev.log')
    ?? files[0];

  const applyPreset = (preset: LogPreset) => {
    if (preset.id === 'all') {
      const preferred = preferredLogFile();
      if (preferred) {
        setSelectedFile(preferred.name);
      }
      setSearchInput('');
      setAppliedSearch('');
      setDateInput('');
      setAppliedDate('');
      setHourInput('');
      setAppliedHour('');
      return;
    }

    if (preset.file && files.some((file) => file.name === preset.file)) {
      setSelectedFile(preset.file);
    } else if (!preset.file && selectedFile === 'deprecation.log') {
      const preferred = preferredLogFile();
      if (preferred) {
        setSelectedFile(preferred.name);
      }
    }

    setSearchInput(preset.search);
    setAppliedSearch(preset.search);
  };

  const activePresetId = useMemo(() => {
    const presets = getPresetsForFile(selectedFile);
    const deprecationPreset = LOG_PRESETS.find((preset) => preset.id === 'deprecations');
    if (
      deprecationPreset?.file
      && selectedFile === deprecationPreset.file
      && appliedSearch === ''
    ) {
      return 'deprecations';
    }

    const match = presets.find(
      (preset) => preset.id !== 'deprecations' && preset.search === appliedSearch,
    );
    if (match) {
      return match.id;
    }

    return appliedSearch === '' && selectedFile !== 'deprecation.log' ? 'all' : null;
  }, [appliedSearch, selectedFile]);

  const quickPresets = getPresetsForFile(selectedFile);
  const criticalCount = entries.filter((entry) => entry.is_critical_alert || entry.severity === 'critical').length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Logs Symfony
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Lignes structurées pour repérer vite bugs, mails, comptes et déploiements.
        Les CRITICAL partent aussi vers <code>logs@sherlook.fr</code> (throttle 1 s).
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr 1fr auto' },
              alignItems: 'end',
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="symfony-log-file-label">Fichier</InputLabel>
              <Select
                labelId="symfony-log-file-label"
                label="Fichier"
                value={selectedFile}
                onChange={(event) => setSelectedFile(event.target.value)}
              >
                {files.map((file) => (
                  <MenuItem key={file.name} value={file.name}>
                    {file.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="symfony-log-lines-label">Lignes</InputLabel>
              <Select
                labelId="symfony-log-lines-label"
                label="Lignes"
                value={lines}
                onChange={(event) => setLines(Number(event.target.value))}
              >
                {LINE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="date"
              label="Jour"
              value={dateInput}
              onChange={(event) => setDateInput(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth>
              <InputLabel id="symfony-log-hour-label">Heure</InputLabel>
              <Select
                labelId="symfony-log-hour-label"
                label="Heure"
                value={hourInput}
                onChange={(event) => {
                  const value = event.target.value;
                  setHourInput(value === '' ? '' : Number(value));
                }}
              >
                <MenuItem value="">
                  <em>Toutes</em>
                </MenuItem>
                {HOUR_OPTIONS.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {formatHourLabel(hour)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Recherche"
              placeholder="CRITICAL, email, Doctrine…"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleApplyFilters();
                }
              }}
            />

            <Button
              variant="contained"
              onClick={() => {
                handleApplyFilters();
                if (selectedFile) {
                  void refetch();
                }
              }}
              disabled={!selectedFile || isFetching}
            >
              Actualiser
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Filtres rapides
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickPresets.map((preset) => (
                <Chip
                  key={preset.id}
                  label={preset.label}
                  size="small"
                  clickable
                  color={activePresetId === preset.id ? 'primary' : 'default'}
                  variant={activePresetId === preset.id ? 'filled' : 'outlined'}
                  onClick={() => applyPreset(preset)}
                />
              ))}
            </Box>
          </Box>

          {selectedFileMeta && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Taille : {formatFileSize(selectedFileMeta.size)} — Modifié le {formatModifiedAt(selectedFileMeta.modified_at)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {!selectedFile && (
        <Alert severity="info">
          Sélectionnez un fichier de log pour afficher les dernières lignes.
        </Alert>
      )}

      {selectedFile && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {selectedFile && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les logs.
        </Alert>
      )}

      {selectedFile && data && !isLoading && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {entries.length} ligne{entries.length > 1 ? 's' : ''}
              {data.truncated ? ' (tronqué)' : ''}
              {criticalCount > 0 ? ` — ${criticalCount} critique${criticalCount > 1 ? 's' : ''}` : ''}
              {data.date ? ` — jour ${data.date}` : ''}
              {data.hour !== null && data.hour !== undefined ? ` — ${formatHourLabel(data.hour)}` : ''}
              {data.search ? ` — texte « ${data.search} »` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowRawFallback((value) => !value)}
              >
                {showRawFallback ? 'Vue tableau' : 'Vue brute'}
              </Button>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                Rafraîchir
              </Button>
            </Box>
          </Box>

          {showRawFallback ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                bgcolor: '#0f172a',
                color: '#e2e8f0',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '70vh',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {(data.lines ?? []).length === 0 ? (
                <Box component="span" sx={{ color: '#94a3b8' }}>
                  Aucune ligne à afficher.
                </Box>
              ) : (
                (data.lines ?? []).map((line, index) => (
                  <Box key={`${index}-${line.slice(0, 24)}`} component="span" sx={{ display: 'block' }}>
                    {line}
                  </Box>
                ))
              )}
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: '70vh',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: '#fff',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                maxWidth: '100%',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: { xs: 720, md: 1100 } }}>
                <TableHead>
                  <TableRow>
                    {['Date / Heure', 'Niveau', 'Type', 'Message', 'Exception', 'Code', 'Fichier', 'Ligne', ''].map((label) => (
                      <TableCell
                        key={label || 'expand'}
                        sx={{
                          fontWeight: 700,
                          bgcolor: '#f8fafc',
                          whiteSpace: 'nowrap',
                          fontSize: 12,
                          color: 'text.secondary',
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                          Aucune ligne à afficher.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry, index) => {
                      const colors = levelColors(entry.level);
                      const typeMeta = categoryMeta(entry.category);
                      const open = Boolean(expandedRaw[index]);

                      return (
                        <React.Fragment key={`${index}-${entry.datetime ?? ''}-${entry.message.slice(0, 40)}`}>
                          <TableRow
                            hover
                            sx={{
                              bgcolor: rowBackground(entry),
                              '& td': { borderColor: '#e2e8f0', verticalAlign: 'top', py: 1.25 },
                            }}
                          >
                            <TableCell sx={{ minWidth: 150 }}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.35 }} />
                                <Box>
                                  <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>
                                    {entry.datetime_label ?? '—'}
                                  </Typography>
                                  {entry.timezone && (
                                    <Typography variant="caption" color="text.secondary">
                                      {entry.timezone}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ minWidth: 120 }}>
                              <Chip
                                size="small"
                                label={entry.level}
                                sx={{
                                  bgcolor: colors.bg,
                                  color: colors.fg,
                                  fontWeight: 700,
                                  fontSize: 11,
                                  height: 22,
                                  mb: 0.5,
                                }}
                              />
                              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.bg }}>
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: colors.bg }} />
                                {entry.channel}.{entry.level}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ minWidth: 90 }}>
                              <Box sx={{ color: typeMeta.color, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                                {typeMeta.icon}
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: typeMeta.color }}>
                                  {entry.category}
                                </Typography>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ minWidth: 260, maxWidth: 420 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 13, lineHeight: 1.35 }}>
                                {entry.message}
                              </Typography>
                              {entry.message_secondary && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                  {entry.message_secondary}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ minWidth: 160 }}>
                              {entry.exception ? (
                                <>
                                  <Chip
                                    size="small"
                                    label={entry.exception}
                                    sx={{
                                      bgcolor: colors.soft,
                                      color: colors.bg,
                                      fontWeight: 600,
                                      fontSize: 11,
                                      height: 22,
                                      mb: 0.5,
                                      maxWidth: 200,
                                    }}
                                  />
                                  {entry.exception_chain && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 220 }}>
                                      {entry.exception_chain}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ minWidth: 90 }}>
                              {entry.http_code ? (
                                <>
                                  <Chip
                                    size="small"
                                    label={String(entry.http_code)}
                                    sx={{
                                      bgcolor: colors.soft,
                                      color: colors.bg,
                                      fontWeight: 700,
                                      fontSize: 11,
                                      height: 22,
                                      mb: 0.5,
                                    }}
                                  />
                                  {entry.http_label && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {entry.http_label}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ minWidth: 140 }}>
                              {entry.file ? (
                                <>
                                  <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                    {entry.file}
                                  </Typography>
                                  {entry.file_path && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 180 }}>
                                      {entry.file_path}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ fontWeight: 700, fontSize: 13, width: 56 }}>
                              {entry.line ?? '—'}
                            </TableCell>

                            <TableCell sx={{ width: 40 }}>
                              <Tooltip title="Ligne brute">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExpandedRaw((prev) => ({ ...prev, [index]: !prev[index] }))
                                  }
                                >
                                  {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          {open && (
                            <TableRow>
                              <TableCell colSpan={9} sx={{ bgcolor: '#0f172a', py: 1.5 }}>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    color: '#e2e8f0',
                                    fontSize: 11,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                  }}
                                >
                                  {entry.raw}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default SymfonyLogsPage;
